/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  GridCallbackDetails,
  GridRowParams,
  MuiEvent,
} from '@spotgamma/x-data-grid-premium';
import {
  defaultColumnScannerVisibility,
  scannerFields,
  EQUITYHUB_UPSELL,
} from 'config';
import useAuth from 'hooks/auth/useAuth';
import usePrices from 'hooks/equityhub/usePrices';
import {
  synthesizedEquitiesState,
  pricesState,
  equitiesFetchLoadingState,
  watchlistsState,
} from 'states';
import { ProductType, ScannerID, LegacyEquity, SynthOIEquity } from 'types';
import { CanvasComponentConfig } from 'types/canvas';
import {
  hasKeyEquityFields,
  getColumnsForScanner,
  getScannerData,
} from 'util/equityhub';
import {
  useEquityGridColumns,
  EquityTableType,
} from 'components/equityhub/EquityhubTable/useEquityScannerGridColumns';
import { UpsellModal, WatchlistMultiSelect } from 'components/shared';
import { EquityScannerGrid } from 'components/stock_scanner/EquityScannerGrid';
import CanvasScannersSection from './CanvasScannersSection';
import {
  useScannerGridState,
  ScannerGridStateSnapshot,
} from 'hooks/scanners/useScannerGridState';

interface CanvasEquitiesScannerProps {
  config: CanvasComponentConfig;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const COMPACT_LIMIT_WIDTH = 1_100;

/** Extract grid state snapshot from canvas component config. */
function getGridInitial(
  config: CanvasComponentConfig,
): ScannerGridStateSnapshot {
  const grid = config.gridState as ScannerGridStateSnapshot | undefined;
  return grid ?? {};
}

const CanvasEquitiesScanner = ({
  config,
  onConfigChange,
}: CanvasEquitiesScannerProps) => {
  const theme = useTheme();
  const equities = useRecoilValue(synthesizedEquitiesState);
  const setPrices = useSetRecoilState(pricesState);
  const equitiesLoading = useRecoilValue(equitiesFetchLoadingState);
  const watchlists = useRecoilValue(watchlistsState);
  const { hasAccessToProduct } = useAuth();
  const hasAccess = hasAccessToProduct(ProductType.SCANNERS);
  const [upsellOpen, setUpsellOpen] = useState<boolean>(false);
  const { getPrices } = usePrices();
  const [pricesLoading, setPricesLoading] = useState<boolean>(false);

  const isFirstSync = useRef<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState<boolean>(false);

  // Grid internals — hydrated from persisted canvas config
  const gridState = useScannerGridState(getGridInitial(config));

  // Sync grid state changes back to canvas config (persisted to DB)
  const gridSnapshot: ScannerGridStateSnapshot = useMemo(
    () => ({
      useDefaultColumns: gridState.useDefaultColumns,
      columnVisibilityModel: gridState.columnVisibilityModel,
      pinnedColumns: gridState.pinnedColumns,
      sortModel: gridState.sortModel,
      filterModel: gridState.filterModel,
      columnOrder: gridState.columnOrder,
      activeWatchlistIds: gridState.activeWatchlistIds,
    }),
    [
      gridState.useDefaultColumns,
      gridState.columnVisibilityModel,
      gridState.pinnedColumns,
      gridState.sortModel,
      gridState.filterModel,
      gridState.columnOrder,
      gridState.activeWatchlistIds,
    ],
  );

  useEffect(() => {
    // Skip the initial mount — config already has this data
    if (isFirstSync.current != null) {
      isFirstSync.current = false;
      return;
    }
    onConfigChange({ gridState: gridSnapshot });
  }, [gridSnapshot]);

  useEffect(() => {
    const el = containerRef.current;
    if (el == null) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setIsCompact(entry.contentRect.width < COMPACT_LIMIT_WIDTH);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const activeScanner =
    (config.scanner as ScannerID | undefined) ?? ScannerID.VOL_RISK_PREMIUM;

  const { columns: gridCols, columnGroups: gridColGroups } =
    useEquityGridColumns(
      theme,
      undefined,
      EquityTableType.StockScreener,
      true,
      !hasAccess
        ? [...equities.values()]
            .filter((e) => !hasKeyEquityFields(e))
            .map((e) => e.sym)
        : undefined,
    );

  useEffect(() => {
    if (gridState.useDefaultColumns) {
      gridState.setColumnVisibilityModel(
        activeScanner
          ? getColumnsForScanner(activeScanner)
          : defaultColumnScannerVisibility,
      );
    }
  }, [gridState.useDefaultColumns, activeScanner]);

  useEffect(() => {
    async function fetchVisiblePriceData() {
      try {
        setPricesLoading(true);
        const newPrices: Map<string, number> = await getPrices([]);
        setPrices((prices) => new Map([...prices, ...newPrices]));
      } finally {
        setPricesLoading(false);
      }
    }
    fetchVisiblePriceData();
  }, [getPrices, setPrices]);

  const scannerData = useMemo(
    () =>
      getScannerData({
        equities,
        watchlists,
        activeWatchlistIds: gridState.activeWatchlistIds,
        activeScanner,
        hasAccess,
      }),
    [
      activeScanner,
      gridState.activeWatchlistIds,
      watchlists,
      equities,
      hasAccess,
    ],
  );

  const isRowDisabled = (row: LegacyEquity | SynthOIEquity) => {
    return !hasAccess && !row.live;
  };

  const onRowClick = (
    params: GridRowParams,
    _event: MuiEvent,
    _details: GridCallbackDetails,
  ) => {
    const row = params.row as LegacyEquity | SynthOIEquity;
    if (isRowDisabled(row)) {
      setUpsellOpen(true);
    }
  };

  const handleScannerChange = (scanner: ScannerID | undefined) => {
    onConfigChange({ scanner: scanner ?? null });
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <EquityScannerGrid
        columns={gridCols}
        data={scannerData}
        columnGroups={gridColGroups}
        onRowClick={onRowClick}
        loading={equitiesLoading || pricesLoading}
        activeScanner={activeScanner}
        activeWatchlistIds={gridState.activeWatchlistIds}
        useDefaultColumns={gridState.useDefaultColumns}
        setUseDefaultColumns={gridState.setUseDefaultColumns}
        columnVisibilityModel={gridState.columnVisibilityModel}
        setColumnVisibilityModel={gridState.setColumnVisibilityModel}
        pinnedColumns={gridState.pinnedColumns}
        setPinnedColumns={gridState.setPinnedColumns}
        sortModel={gridState.sortModel}
        setSortModel={gridState.setSortModel}
        filterModel={gridState.filterModel}
        setFilterModel={gridState.setFilterModel}
        columnOrder={gridState.columnOrder}
        setColumnOrder={gridState.setColumnOrder}
        defaultColumnOrder={scannerFields}
        customSettings={
          <WatchlistMultiSelect
            activeWatchlistIds={gridState.activeWatchlistIds}
            setActiveWatchlistIds={gridState.setActiveWatchlistIds}
          />
        }
        customScannerSettings={
          <CanvasScannersSection
            activeScanner={activeScanner}
            onScannerChange={handleScannerChange}
          />
        }
        showScannerInfo
        compact={isCompact}
      />
      <UpsellModal
        open={upsellOpen}
        setOpen={setUpsellOpen}
        title={EQUITYHUB_UPSELL.title}
        subtitle={EQUITYHUB_UPSELL.subtitle}
        items={EQUITYHUB_UPSELL.items}
      />
    </Box>
  );
};

export default CanvasEquitiesScanner;
